/**
 * useHeadshotUrl — resolves a viewable, presigned URL for the most recent
 * HEADSHOT document in an already-fetched document list, for surfaces that
 * want a contestant's official pageant headshot to take display priority over
 * their separately-uploaded, evergreen profile picture.
 *
 * Deliberately takes a document LIST rather than fetching one itself: callers
 * (the director review surface, the Community record page, the contestant's
 * own application view) already fetch their applicant's documents for other
 * reasons (the Documents table, the record's related-documents list, the
 * upload-status UI) — this hook adds no new document read, only the
 * presign-and-cache step for whichever one turns out to be the headshot. The
 * `pickMostRecentHeadshot` choice itself lives in the pure
 * `utils/headshotResolution.ts` so it stays independently testable.
 *
 * Returns `null` while unresolved (no headshot, still loading, or the presign
 * failed) — callers pass this straight through as `LegacyAvatar`'s `directUrl`,
 * which already falls back to `profilePhotoS3Key` then initials on its own.
 */

import { useEffect, useState } from 'react';
import { generateClient } from 'aws-amplify/api';
import type { GraphQLResult } from 'aws-amplify/api';

import { getDocumentDownloadUrl } from '@/graphql/queries';
import { pickMostRecentHeadshot, type HeadshotCandidate } from '@/utils/headshotResolution';

const client = generateClient();

// Module-level cache, same precedent as ApplicantList.tsx's headshotUrlCache —
// a presigned URL is safe to reuse across renders/components for the same
// document id within the page's lifetime.
const urlCache = new Map<string, string>();

export function useHeadshotUrl(
  documents: readonly HeadshotCandidate[] | undefined,
): string | null {
  const headshot = pickMostRecentHeadshot(documents ?? []);
  const headshotId = headshot?.id ?? null;
  const [url, setUrl] = useState<string | null>(() =>
    headshotId ? urlCache.get(headshotId) ?? null : null,
  );

  useEffect(() => {
    if (!headshotId) {
      setUrl(null);
      return;
    }
    const cached = urlCache.get(headshotId);
    if (cached) {
      setUrl(cached);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = (await client.graphql({
          query: getDocumentDownloadUrl,
          variables: { documentId: headshotId },
          authMode: 'userPool',
        })) as GraphQLResult<{ getDocumentDownloadUrl: string }>;
        const resolved = res.data?.getDocumentDownloadUrl;
        if (resolved) urlCache.set(headshotId, resolved);
        if (!cancelled) setUrl(resolved ?? null);
      } catch (err) {
        console.warn(`Failed to fetch headshot download URL for document ${headshotId}:`, err);
        if (!cancelled) setUrl(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [headshotId]);

  return url;
}
